import api from './api';

export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  action?: AIAction;
  suggestions?: SmartSuggestion[];
}

export interface AIAction {
  type: 'create_deck' | 'create_quiz' | 'create_match' | 'navigate' | 'info';
  payload?: {
    topic?: string;
    title?: string;
    description?: string;
    cardCount?: number;
    questionCount?: number;
    pairsCount?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'easy' | 'medium' | 'hard';
    language?: 'portuguese' | 'english' | 'spanish';
    navigateTo?: string;
    questionTypes?: {
      multipleChoice?: number;
      trueFalse?: number;
      fillBlank?: number;
      essay?: number;
    };
  };
}

// Smart suggestions from AI parsing
export interface SmartSuggestion {
  id: string;
  type: 'quick_action' | 'topic_suggestion' | 'follow_up';
  label: string;
  action?: {
    type: 'create_deck' | 'create_quiz' | 'create_match' | 'navigate' | 'send_message';
    payload: Record<string, any>;
  };
}

// Parsed intent from AI
export interface ParsedUserIntent {
  intent: 'create_deck' | 'create_quiz' | 'create_match' | 'navigate' | 'help' | 'general_question' | 'study_request' | 'unknown';
  confidence: number;
  extractedParams: {
    topic?: string;
    count?: number;
    difficulty?: string;
    language?: string;
    navigateTo?: string;
  };
  suggestedResponse: string;
  suggestions: SmartSuggestion[];
  clarificationNeeded?: boolean;
  clarificationQuestion?: string;
}

export interface AICreateRequest {
  type: 'deck' | 'quiz' | 'match';
  topic: string;
  description?: string;
  count?: number;
  difficulty?: string;
  language?: string;
}

export const aiChatService = {
  /**
   * Smart parse user intent using AI backend
   * Falls back to basic parsing if API call fails
   */
  async smartParseIntent(
    message: string,
    conversationContext: { role: 'user' | 'assistant'; content: string }[] = []
  ): Promise<ParsedUserIntent> {
    try {
      const response = await api.post('/ai/parse-intent', {
        message,
        conversationContext,
      });
      
      if (response.data.success) {
        return response.data.intent;
      }
      
      // Fallback to local parsing
      return this.localParseIntent(message);
    } catch (error) {
      console.warn('Smart parsing failed, using local parsing:', error);
      return this.localParseIntent(message);
    }
  },

  /**
   * Get initial suggestions for the chat
   */
  async getInitialSuggestions(): Promise<SmartSuggestion[]> {
    try {
      const response = await api.get('/ai/suggestions');
      return response.data.suggestions || [];
    } catch (error) {
      console.warn('Failed to get suggestions:', error);
      return this.getDefaultSuggestions();
    }
  },

  /**
   * Default suggestions when API is unavailable
   */
  getDefaultSuggestions(isPortuguese: boolean = true): SmartSuggestion[] {
    return [
      {
        id: 'deck_quick',
        type: 'quick_action',
        label: isPortuguese ? '📚 Criar Flashcards' : '📚 Create Flashcards',
        action: { type: 'send_message', payload: { message: isPortuguese ? 'Quero criar um deck' : 'I want to create a deck' } }
      },
      {
        id: 'quiz_quick',
        type: 'quick_action',
        label: isPortuguese ? '🧩 Criar Quiz' : '🧩 Create Quiz',
        action: { type: 'send_message', payload: { message: isPortuguese ? 'Quero criar um quiz' : 'I want to create a quiz' } }
      },
      {
        id: 'match_quick',
        type: 'quick_action',
        label: isPortuguese ? '✨ Criar Match' : '✨ Create Match',
        action: { type: 'send_message', payload: { message: isPortuguese ? 'Quero criar um match' : 'I want to create a match' } }
      },
      {
        id: 'help_quick',
        type: 'quick_action',
        label: isPortuguese ? '❓ Ajuda' : '❓ Help',
        action: { type: 'send_message', payload: { message: isPortuguese ? 'ajuda' : 'help' } }
      }
    ];
  },

  /**
   * Detect language from conversation context and current message
   * Prioritizes context language to maintain consistency in the conversation
   */
  detectLanguageFromContext(message: string, conversationContext: { role: 'user' | 'assistant'; content: string }[] = []): 'portuguese' | 'english' | 'spanish' {
    // Portuguese indicators
    const portuguesePatterns = /[áàâãéèêíìîóòôõúùûç]|sobre|criar|quero|ajuda|como|você|quer|fazer|estudar|aprender|cartões|questões|pares|confirmar|ótimo|perfeito|excelente|tema|baralho|jogo/i;
    // Spanish indicators (ñ and ¿/¡ are unmistakably Spanish, unlike Portuguese)
    const spanishPatterns = /[ñ¿¡]|quiero|ayuda|cómo|quieres|crear|tarjetas|preguntas|pares|confirmar|genial|perfecto|excelente|tema|mazo|juego|estudiar|aprender/i;
    // English indicators  
    const englishPatterns = /\b(create|want|help|about|topic|cards|questions|pairs|confirm|great|perfect|excellent|would|like|study|learn)\b/i;
    
    // First, check conversation context (user messages) to determine established language
    const userMessages = conversationContext.filter(m => m.role === 'user');
    let portugueseScore = 0;
    let spanishScore = 0;
    let englishScore = 0;
    
    for (const msg of userMessages) {
      if (portuguesePatterns.test(msg.content)) {
        portugueseScore += 2; // Weight context higher
      }
      if (spanishPatterns.test(msg.content)) {
        spanishScore += 2;
      }
      if (englishPatterns.test(msg.content)) {
        englishScore += 2;
      }
    }
    
    // Then check current message
    if (portuguesePatterns.test(message)) {
      portugueseScore += 1;
    }
    if (spanishPatterns.test(message)) {
      spanishScore += 1;
    }
    if (englishPatterns.test(message)) {
      englishScore += 1;
    }
    
    // If no clear indicators, default to Portuguese (app's primary language)
    if (portugueseScore === 0 && spanishScore === 0 && englishScore === 0) {
      return 'portuguese';
    }
    
    if (portugueseScore >= spanishScore && portugueseScore >= englishScore) {
      return 'portuguese';
    }
    return spanishScore >= englishScore ? 'spanish' : 'english';
  },

  /**
   * Local intent parsing - fallback when API is unavailable
   */
  localParseIntent(message: string, conversationContext: { role: 'user' | 'assistant'; content: string }[] = []): ParsedUserIntent {
    const { intent, params } = this.parseUserIntent(message, conversationContext);
    
    // Detect language from context to maintain conversation consistency
    const detectedLanguage = this.detectLanguageFromContext(message, conversationContext);
    const isPortuguese = detectedLanguage === 'portuguese';
    
    let suggestedResponse = '';
    let suggestions: SmartSuggestion[] = [];
    let clarificationNeeded = false;
    let clarificationQuestion = '';

    switch (intent) {
      case 'create_deck':
        if (!params.topic) {
          clarificationNeeded = true;
          clarificationQuestion = isPortuguese ? 'Sobre qual tema?' : 'About what topic?';
          suggestedResponse = isPortuguese
            ? '📚 Vou te ajudar a criar um deck! Sobre qual tema você gostaria de estudar?'
            : '📚 I\'ll help you create a deck! What topic would you like to study?';
        } else if (!params.count) {
          clarificationNeeded = true;
          clarificationQuestion = isPortuguese ? 'Quantos cartões?' : 'How many cards?';
          suggestedResponse = isPortuguese
            ? `📚 Ótimo! Quantos cartões você gostaria no deck sobre "${params.topic}"?`
            : `📚 Great! How many cards would you like in the deck about "${params.topic}"?`;
        } else {
          suggestedResponse = isPortuguese
            ? `📚 Perfeito! Vou criar um deck sobre "${params.topic}" com ${params.count} cartões. Confirme para continuar.`
            : `📚 Perfect! I'll create a deck about "${params.topic}" with ${params.count} cards. Confirm to continue.`;
          suggestions.push({
            id: 'confirm_deck',
            type: 'quick_action',
            label: isPortuguese ? '✅ Confirmar' : '✅ Confirm',
            action: { type: 'create_deck', payload: params }
          });
        }
        break;
      case 'create_quiz':
        if (!params.topic) {
          clarificationNeeded = true;
          clarificationQuestion = isPortuguese ? 'Sobre qual tema?' : 'About what topic?';
          suggestedResponse = isPortuguese
            ? '🧩 Vou te ajudar a criar um quiz! Sobre qual tema?'
            : '🧩 I\'ll help you create a quiz! About what topic?';
        } else if (!params.count) {
          clarificationNeeded = true;
          clarificationQuestion = isPortuguese ? 'Quantas questões?' : 'How many questions?';
          suggestedResponse = isPortuguese
            ? `🧩 Excelente! Quantas questões você gostaria no quiz sobre "${params.topic}"?`
            : `🧩 Excellent! How many questions would you like in the quiz about "${params.topic}"?`;
        } else {
          suggestedResponse = isPortuguese
            ? `🧩 Perfeito! Vou criar um quiz sobre "${params.topic}" com ${params.count} questões. Confirme para continuar.`
            : `🧩 Perfect! I'll create a quiz about "${params.topic}" with ${params.count} questions. Confirm to continue.`;
          suggestions.push({
            id: 'confirm_quiz',
            type: 'quick_action',
            label: isPortuguese ? '✅ Confirmar' : '✅ Confirm',
            action: { type: 'create_quiz', payload: params }
          });
        }
        break;
      case 'create_match':
        if (!params.topic) {
          clarificationNeeded = true;
          clarificationQuestion = isPortuguese ? 'Sobre qual tema?' : 'About what topic?';
          suggestedResponse = isPortuguese
            ? '✨ Vou te ajudar a criar um match! Sobre qual tema?'
            : '✨ I\'ll help you create a match! About what topic?';
        } else if (!params.count) {
          clarificationNeeded = true;
          clarificationQuestion = isPortuguese ? 'Quantos pares?' : 'How many pairs?';
          suggestedResponse = isPortuguese
            ? `✨ Ótimo! Quantos pares você gostaria no match sobre "${params.topic}"?`
            : `✨ Great! How many pairs would you like in the match about "${params.topic}"?`;
        } else {
          suggestedResponse = isPortuguese
            ? `✨ Perfeito! Vou criar um match sobre "${params.topic}" com ${params.count} pares. Confirme para continuar.`
            : `✨ Perfect! I'll create a match about "${params.topic}" with ${params.count} pairs. Confirm to continue.`;
          suggestions.push({
            id: 'confirm_match',
            type: 'quick_action',
            label: isPortuguese ? '✅ Confirmar' : '✅ Confirm',
            action: { type: 'create_match', payload: params }
          });
        }
        break;
      case 'help':
        suggestedResponse = isPortuguese
          ? `🤖 Posso te ajudar com:\n\n📚 **Flashcards** - "Criar deck sobre JavaScript"\n🧩 **Quizzes** - "Criar quiz sobre história"\n✨ **Matches** - "Criar match sobre capitais"\n\nO que você gostaria de criar?`
          : `🤖 I can help you with:\n\n📚 **Flashcards** - "Create deck about JavaScript"\n🧩 **Quizzes** - "Create quiz about history"\n✨ **Matches** - "Create match about capitals"\n\nWhat would you like to create?`;
        suggestions = this.getDefaultSuggestions(isPortuguese).slice(0, 3);
        break;
      case 'study_request':
        if (params.topic) {
          suggestedResponse = isPortuguese
            ? `Ótimo! Para aprender sobre "${params.topic}", recomendo:\n\n📚 **Flashcards** - Ideais para memorizar conceitos e vocabulário\n🧩 **Quiz** - Perfeito para testar seu conhecimento na prática\n✨ **Match** - Ótimo para fixar rapidamente de forma divertida\n\nEscolha uma opção abaixo para começar:`
            : `Great! To learn about "${params.topic}", I recommend:\n\n📚 **Flashcards** - Ideal for memorizing concepts and vocabulary\n🧩 **Quiz** - Perfect for testing your knowledge in practice\n✨ **Match** - Great for quick and fun reinforcement\n\nChoose an option below to start:`;
          suggestions = [
            { id: 'deck_' + Date.now(), type: 'quick_action', label: isPortuguese ? '📚 Flashcards' : '📚 Flashcards', action: { type: 'send_message', payload: { message: isPortuguese ? `Flashcards sobre ${params.topic}` : `Flashcards about ${params.topic}` } } },
            { id: 'quiz_' + Date.now(), type: 'quick_action', label: isPortuguese ? '🧩 Quiz' : '🧩 Quiz', action: { type: 'send_message', payload: { message: isPortuguese ? `Quiz sobre ${params.topic}` : `Quiz about ${params.topic}` } } },
            { id: 'match_' + Date.now(), type: 'quick_action', label: isPortuguese ? '✨ Match' : '✨ Match', action: { type: 'send_message', payload: { message: isPortuguese ? `Match sobre ${params.topic}` : `Match about ${params.topic}` } } }
          ];
        } else {
          clarificationNeeded = true;
          suggestedResponse = isPortuguese ? '📖 O que você gostaria de estudar?' : '📖 What would you like to study?';
        }
        break;
      case 'navigate':
        if (params.navigateTo) {
          const moduleNames: Record<string, { pt: string, en: string }> = {
            '/decks': { pt: 'Flashcards', en: 'Flashcards' },
            '/quizzes': { pt: 'Quizzes', en: 'Quizzes' },
            '/matches': { pt: 'Match', en: 'Match' }
          };
          const moduleName = moduleNames[params.navigateTo as string];
          
          if (params.topic) {
            suggestedResponse = isPortuguese
              ? `Perfeito! Acesse [${moduleName.pt}](${params.navigateTo}) para começar a estudar "${params.topic}". Você encontrará todos os recursos disponíveis lá! 🚀`
              : `Perfect! Go to [${moduleName.en}](${params.navigateTo}) to start studying "${params.topic}". You'll find all available resources there! 🚀`;
          } else {
            suggestedResponse = isPortuguese
              ? `Excelente escolha! Vá para [${moduleName.pt}](${params.navigateTo}) para explorar os recursos disponíveis. 🚀`
              : `Excellent choice! Go to [${moduleName.en}](${params.navigateTo}) to explore available resources. 🚀`;
          }
        } else {
          clarificationNeeded = true;
          suggestedResponse = isPortuguese
            ? '🤔 Para onde você gostaria de ir?'
            : '🤔 Where would you like to go?';
        }
        break;
      default:
        clarificationNeeded = true;
        suggestedResponse = isPortuguese
          ? '🤔 Não entendi completamente. Posso te ajudar a criar flashcards, quizzes ou jogos de match. O que você gostaria de fazer?'
          : '🤔 I didn\'t quite understand. I can help you create flashcards, quizzes, or match games. What would you like to do?';
        suggestions = this.getDefaultSuggestions(isPortuguese);
    }

    return {
      intent: intent as ParsedUserIntent['intent'],
      confidence: 0.6,
      extractedParams: params,
      suggestedResponse,
      suggestions,
      clarificationNeeded,
      clarificationQuestion
    };
  },

  /**
   * Generate a chat response from parsed intent
   */
  generateResponseFromIntent(parsedIntent: ParsedUserIntent): AIChatMessage {
    const id = `msg_${Date.now()}`;
    
    // Build action based on intent - only when topic AND count are provided
    let action: AIAction | undefined;
    
    if (parsedIntent.intent === 'create_deck' && parsedIntent.extractedParams.topic && parsedIntent.extractedParams.count) {
      action = {
        type: 'create_deck',
        payload: {
          topic: parsedIntent.extractedParams.topic,
          cardCount: parsedIntent.extractedParams.count,
          difficulty: (parsedIntent.extractedParams.difficulty as any) || 'intermediate',
          language: (parsedIntent.extractedParams.language as any) || 'portuguese',
        },
      };
    } else if (parsedIntent.intent === 'create_quiz' && parsedIntent.extractedParams.topic && parsedIntent.extractedParams.count) {
      action = {
        type: 'create_quiz',
        payload: {
          topic: parsedIntent.extractedParams.topic,
          questionCount: parsedIntent.extractedParams.count,
          difficulty: (parsedIntent.extractedParams.difficulty as any) || 'intermediate',
          language: (parsedIntent.extractedParams.language as any) || 'portuguese',
        },
      };
    } else if (parsedIntent.intent === 'create_match' && parsedIntent.extractedParams.topic && parsedIntent.extractedParams.count) {
      action = {
        type: 'create_match',
        payload: {
          topic: parsedIntent.extractedParams.topic,
          pairsCount: parsedIntent.extractedParams.count,
          difficulty: (parsedIntent.extractedParams.difficulty as any) || 'medium',
          language: (parsedIntent.extractedParams.language as any) || 'portuguese',
        },
      };
    } else if (parsedIntent.intent === 'navigate' && parsedIntent.extractedParams.navigateTo) {
      action = {
        type: 'navigate',
        payload: { navigateTo: parsedIntent.extractedParams.navigateTo },
      };
    }

    return {
      id,
      role: 'assistant',
      content: parsedIntent.suggestedResponse,
      timestamp: new Date(),
      action,
      suggestions: parsedIntent.suggestions,
    };
  },

  // Parse user message to detect intent (legacy - kept for compatibility)
  parseUserIntent(message: string, conversationContext: { role: 'user' | 'assistant'; content: string }[] = []): { intent: string; params: Record<string, any> } {
    const lowerMessage = message.toLowerCase().trim();
    
    // Extract topic from conversation context if not in current message
    const extractTopicFromContext = (): string | undefined => {
      // Look through previous messages for topic mentions
      for (let i = conversationContext.length - 1; i >= 0; i--) {
        const msg = conversationContext[i];
        // Check for "aprender sobre X", "estudar X", "sobre X"
        const topicMatch = msg.content.match(/(?:aprender|estudar|learn|study)\s+(?:sobre\s+)?["']?([^,\.!?]+?)["']?(?:\s|,|\.|\?|!|$)/i) ||
                          msg.content.match(/(?:sobre|about)\s+["']?([^,\.!?]+?)["']?(?:\s|,|\.|\?|!|$)/i) ||
                          msg.content.match(/quiz\s+(?:sobre|about|de)\s+["']?([^,\.!?]+?)["']?(?:\s|,|\.|\?|!|$)/i) ||
                          msg.content.match(/deck\s+(?:sobre|about|de)\s+["']?([^,\.!?]+?)["']?(?:\s|,|\.|\?|!|$)/i) ||
                          msg.content.match(/match\s+(?:sobre|about|de)\s+["']?([^,\.!?]+?)["']?(?:\s|,|\.|\?|!|$)/i) ||
                          msg.content.match(/flashcards?\s+(?:sobre|about|de)\s+["']?([^,\.!?]+?)["']?(?:\s|,|\.|\?|!|$)/i);
        if (topicMatch) {
          return topicMatch[1].trim();
        }
      }
      return undefined;
    };

    // Check if user chose a method after seeing study recommendations
    const methodChoicePattern = /^(flashcards?|quiz|match|cartões|baralho|questionário|prova|jogo|pareamento)\s+(?:sobre|about|de)?\s*(.+)?$/i;
    const methodMatch = lowerMessage.match(methodChoicePattern);
    
    if (methodMatch) {
      const method = methodMatch[1].toLowerCase();
      const topicFromMessage = methodMatch[2]?.trim();
      const topic = topicFromMessage || extractTopicFromContext();
      
      if (method.match(/flashcards?|cartões|baralho|deck|cards/i)) {
        return { intent: 'navigate', params: { navigateTo: '/decks', topic } };
      } else if (method.match(/quiz|questionário|prova/i)) {
        return { intent: 'navigate', params: { navigateTo: '/quizzes', topic } };
      } else if (method.match(/match|jogo|pareamento/i)) {
        return { intent: 'navigate', params: { navigateTo: '/matches', topic } };
      }
    }

    // Detect simple single-word intents (e.g., just "Quiz", "Deck", "Match")
    const simpleQuizPattern = /^(quiz|questionário|prova)$/i;
    const simpleDeckPattern = /^(deck|flashcard|flashcards|cartões|cards|baralho)$/i;
    const simpleMatchPattern = /^(match|jogo|pareamento|jogo de memória)$/i;
    const contextTopic = extractTopicFromContext();

    // If there's a topic in context, navigate directly instead of asking for details
    if (simpleQuizPattern.test(lowerMessage)) {
      if (contextTopic) {
        return { intent: 'navigate', params: { navigateTo: '/quizzes', topic: contextTopic } };
      }
      const params = this.extractParams(message);
      return { intent: 'create_quiz', params };
    }

    if (simpleDeckPattern.test(lowerMessage)) {
      if (contextTopic) {
        return { intent: 'navigate', params: { navigateTo: '/decks', topic: contextTopic } };
      }
      const params = this.extractParams(message);
      return { intent: 'create_deck', params };
    }

    if (simpleMatchPattern.test(lowerMessage)) {
      if (contextTopic) {
        return { intent: 'navigate', params: { navigateTo: '/matches', topic: contextTopic } };
      }
      const params = this.extractParams(message);
      return { intent: 'create_match', params };
    }
    
    // Detect deck/flashcard creation intent
    if (
      lowerMessage.includes('criar deck') ||
      lowerMessage.includes('criar flashcard') ||
      lowerMessage.includes('criar cartões') ||
      lowerMessage.includes('criar cards') ||
      lowerMessage.includes('gerar deck') ||
      lowerMessage.includes('gerar flashcard') ||
      lowerMessage.includes('create deck') ||
      lowerMessage.includes('create flashcard')
    ) {
      return { intent: 'create_deck', params: this.extractParams(message) };
    }

    // Detect quiz creation intent
    if (
      lowerMessage.includes('criar quiz') ||
      lowerMessage.includes('criar questionário') ||
      lowerMessage.includes('criar prova') ||
      lowerMessage.includes('gerar quiz') ||
      lowerMessage.includes('create quiz')
    ) {
      return { intent: 'create_quiz', params: this.extractParams(message) };
    }

    // Detect match creation intent
    if (
      lowerMessage.includes('criar match') ||
      lowerMessage.includes('criar jogo') ||
      lowerMessage.includes('criar pareamento') ||
      lowerMessage.includes('gerar match') ||
      lowerMessage.includes('create match')
    ) {
      return { intent: 'create_match', params: this.extractParams(message) };
    }

    // Navigation intents
    if (lowerMessage.includes('meus decks') || lowerMessage.includes('ver decks')) {
      return { intent: 'navigate', params: { to: '/decks' } };
    }
    if (lowerMessage.includes('meus quizzes') || lowerMessage.includes('ver quizzes')) {
      return { intent: 'navigate', params: { to: '/quizzes' } };
    }
    if (lowerMessage.includes('meus matches') || lowerMessage.includes('ver matches')) {
      return { intent: 'navigate', params: { to: '/matches' } };
    }

    // Help intent
    if (
      lowerMessage.includes('ajuda') ||
      lowerMessage.includes('help') ||
      lowerMessage.includes('o que você pode fazer') ||
      lowerMessage.includes('como funciona')
    ) {
      return { intent: 'help', params: {} };
    }

    // Study request intent - user wants to learn something but didn't specify how
    if (
      lowerMessage.includes('quero aprender') ||
      lowerMessage.includes('quero estudar') ||
      lowerMessage.includes('quero praticar') ||
      lowerMessage.includes('aprender sobre') ||
      lowerMessage.includes('estudar sobre') ||
      lowerMessage.includes('want to learn') ||
      lowerMessage.includes('want to study') ||
      lowerMessage.includes('want to practice') ||
      lowerMessage.includes('learn about') ||
      lowerMessage.includes('study about')
    ) {
      return { intent: 'study_request', params: this.extractParams(message) };
    }

    return { intent: 'unknown', params: {} };
  },

  extractParams(message: string): Record<string, any> {
    const params: Record<string, any> = {};
    
    // Extract topic - everything after "sobre" or "about"
    const topicMatch = message.match(/(?:sobre|about|de|com tema)\s+([^,\.]+)/i);
    if (topicMatch) {
      params.topic = topicMatch[1].trim();
    }

    // Extract count
    const countMatch = message.match(/(\d+)\s*(?:cartões|cards|questões|perguntas|pares|pairs)/i);
    if (countMatch) {
      params.count = parseInt(countMatch[1], 10);
    }

    // Extract difficulty
    if (message.toLowerCase().includes('fácil') || message.toLowerCase().includes('easy') || message.toLowerCase().includes('iniciante')) {
      params.difficulty = 'beginner';
    } else if (message.toLowerCase().includes('médio') || message.toLowerCase().includes('medium') || message.toLowerCase().includes('intermediário')) {
      params.difficulty = 'intermediate';
    } else if (message.toLowerCase().includes('difícil') || message.toLowerCase().includes('hard') || message.toLowerCase().includes('avançado')) {
      params.difficulty = 'advanced';
    }

    // Extract language
    if (message.toLowerCase().includes('em inglês') || message.toLowerCase().includes('in english')) {
      params.language = 'english';
    } else if (message.toLowerCase().includes('em português') || message.toLowerCase().includes('in portuguese')) {
      params.language = 'portuguese';
    }

    return params;
  },

  generateResponse(intent: string, params: Record<string, any>): AIChatMessage {
    const id = `msg_${Date.now()}`;
    const timestamp = new Date();

    switch (intent) {
      case 'create_deck':
        if (!params.topic) {
          return {
            id,
            role: 'assistant',
            content: '📚 Vou te ajudar a criar um deck de flashcards! Sobre qual tema você gostaria de estudar?',
            timestamp,
          };
        }
        return {
          id,
          role: 'assistant',
          content: `📚 Ótimo! Vou criar um deck sobre "${params.topic}". ${params.count ? `Com ${params.count} cartões.` : ''} Clique no botão abaixo para confirmar a criação.`,
          timestamp,
          action: {
            type: 'create_deck',
            payload: {
              topic: params.topic,
              cardCount: params.count || 10,
              difficulty: params.difficulty || 'intermediate',
              language: params.language || 'portuguese',
            },
          },
        };

      case 'create_quiz':
        if (!params.topic) {
          return {
            id,
            role: 'assistant',
            content: '🧩 Vou te ajudar a criar um quiz! Sobre qual tema você gostaria de ser testado?',
            timestamp,
          };
        }
        return {
          id,
          role: 'assistant',
          content: `🧩 Excelente! Vou criar um quiz sobre "${params.topic}". ${params.count ? `Com ${params.count} questões.` : ''} Clique no botão abaixo para confirmar a criação.`,
          timestamp,
          action: {
            type: 'create_quiz',
            payload: {
              topic: params.topic,
              questionCount: params.count || 10,
              difficulty: params.difficulty || 'intermediate',
              language: params.language || 'portuguese',
            },
          },
        };

      case 'create_match':
        if (!params.topic) {
          return {
            id,
            role: 'assistant',
            content: '✨ Vou te ajudar a criar um jogo de match! Sobre qual tema você gostaria de jogar?',
            timestamp,
          };
        }
        return {
          id,
          role: 'assistant',
          content: `✨ Perfeito! Vou criar um jogo de match sobre "${params.topic}". ${params.count ? `Com ${params.count} pares.` : ''} Clique no botão abaixo para confirmar a criação.`,
          timestamp,
          action: {
            type: 'create_match',
            payload: {
              topic: params.topic,
              pairsCount: params.count || 8,
              difficulty: params.difficulty || 'medium',
              language: params.language || 'portuguese',
            },
          },
        };

      case 'navigate':
        return {
          id,
          role: 'assistant',
          content: '🚀 Redirecionando você...',
          timestamp,
          action: {
            type: 'navigate',
            payload: { navigateTo: params.to },
          },
        };

      case 'help':
        return {
          id,
          role: 'assistant',
          content: `🤖 Olá! Eu sou seu assistente de aprendizado. Posso te ajudar com:

📚 **Flashcards** - "Criar deck sobre JavaScript"
🧩 **Quizzes** - "Criar quiz sobre história do Brasil"
✨ **Matches** - "Criar match sobre capitais do mundo"

Você pode especificar:
• Quantidade: "10 cartões", "15 questões"
• Dificuldade: "fácil", "médio", "difícil"
• Idioma: "em inglês", "em português"

O que você gostaria de criar hoje?`,
          timestamp,
        };

      default:
        return {
          id,
          role: 'assistant',
          content: `🤔 Não entendi completamente. Posso te ajudar a:

📚 **Criar flashcards** - Ex: "Criar deck sobre Python"
🧩 **Criar quizzes** - Ex: "Criar quiz sobre matemática"
✨ **Criar matches** - Ex: "Criar match sobre verbos em inglês"

Digite o que você deseja criar!`,
          timestamp,
        };
    }
  },

  // Request AI deck creation
  async createAIDeck(params: {
    topic: string;
    cardCount: number;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    language: 'portuguese' | 'english' | 'spanish';
  }): Promise<{ success: boolean; message: string; deckId?: string }> {
    const response = await api.post('/decks/ai-request', {
      topic: params.topic,
      description: `Deck gerado por IA sobre ${params.topic}`,
      cardCount: params.cardCount,
      difficulty: params.difficulty,
      language: params.language,
    });
    return response.data;
  },

  // Request AI quiz creation
  async createAIQuiz(params: {
    topic: string;
    questionCount: number;
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    language: 'portuguese' | 'english' | 'spanish';
    questionTypes?: {
      multipleChoice?: number;
      trueFalse?: number;
      fillBlank?: number;
      essay?: number;
    };
  }): Promise<{ success: boolean; message: string; quizId?: string }> {
    const response = await api.post('/quizzes/ai/generate', {
      topic: params.topic,
      description: `Quiz gerado por IA sobre ${params.topic}`,
      questionCount: params.questionCount,
      difficulty: params.difficulty,
      language: params.language,
      questionTypes: params.questionTypes,
    });
    return response.data;
  },

  // Request AI match creation
  async createAIMatch(params: {
    topic: string;
    pairsCount: number;
    difficulty: 'easy' | 'medium' | 'hard';
    language: 'portuguese' | 'english' | 'spanish';
  }): Promise<{ success: boolean; message: string; matchGameId?: string }> {
    const response = await api.post('/matches/ai/generate', {
      topic: params.topic,
      pairsCount: params.pairsCount,
      difficulty: params.difficulty,
      language: params.language,
    });
    return response.data;
  },
};
